import { useState } from 'react'
import { Screen, SectionHeader } from '@/components/layout'
import { Button } from '@/components/primitives'
import { ImpactCard } from '@/components/cards'
import { GeneratedText } from '@/components/GeneratedText'
import { List, TransactionRow } from '@/components/rows'
import { ErrorState, LoadingRegion, SkeletonCard, SkeletonList } from '@/components/states'
import type { Recommendation } from '@/generated/types'
import { ApiFailure, api } from '@/lib/api'
import { useProseState } from '@/lib/analysis'
import { t, type CopyKey } from '@/lib/copy'
import { categoryName, displayParams, feeKindName, recommendationTitle } from '@/lib/display'
import { formatDate, formatMoney } from '@/lib/format'
import { useNavigate, useRoute } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'

/**
 * The impact, the evidence behind it, and the steps. Every evidence reference
 * navigates to its own detail screen — nothing here is unattributed.
 *
 * The second of the two screens where `analysisFailed` is not silent: the
 * long-form why is what a user opens this screen for, so it earns one quiet
 * retry when the Server says a retry could work. The page header stays keyed
 * in every state — chrome comes from copy.json even on a prose screen.
 */
export function RecommendationDetailScreen() {
  const route = useRoute()
  const navigate = useNavigate()
  const id = route.params.id

  const recommendation = useAsync((signal) => api.recommendations.get(id, signal), [id])
  const rec = recommendation.state.data

  /**
   * The three evidence pools leave WITH the recommendation, not after it.
   *
   * They used to wait for `rec.evidence` to say which of them were needed,
   * which made this screen two round trips deep — and through the edge each
   * link in a chain costs a full RTT to a house, 20–120 ms, while four
   * concurrent calls cost one. The saving is bigger than the waste: an
   * unneeded pool is a few kilobytes on a gzipped frame, and edge-proxy.json
   * requires gzip on responses precisely because those bytes ride the mini's
   * residential uplink.
   *
   * Filtering by the evidence ids stays below, where it always was — the
   * requests are unconditional, the rendering is not.
   */
  const transactionPool = useAsync((signal) => api.transactions.list({ limit: 200 }, signal), [])
  const accounts = useAsync((signal) => api.accounts.list(signal), [])
  const series = useAsync((signal) => api.analysis.recurring(undefined, signal), [])

  const evidenceTransactionIds = rec?.evidence?.transactionIds ?? []
  const evidenceTransactions = (transactionPool.state.data?.items ?? []).filter((tx) =>
    evidenceTransactionIds.includes(tx.id),
  )

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<ApiFailure | null>(null)

  const prose = useProseState(rec?.proseState, recommendation.reload)

  const dataState = saving
    ? 'saving'
    : error
      ? 'error'
      : recommendation.state.status === 'loading' && !rec
        ? 'loading'
        : recommendation.state.status === 'error'
          ? 'error'
          : 'ready'

  // Impact, evidence and the deterministic steps are all present regardless —
  // only the wording of `why` and `steps` differs, so this refines `ready`.
  const screenState = dataState === 'ready' ? (prose.state ?? 'ready') : dataState

  // Both prose states render the identical tree below. `analysisPending` adds
  // no spinner and disables no control; `analysisFailed` is not the error
  // treatment and adds only the one quiet retry at the bottom.
  const proseState: 'analysisPending' | 'analysisFailed' | null =
    screenState === 'analysisPending' || screenState === 'analysisFailed' ? screenState : null

  const setStatus = async (status: Recommendation['status']) => {
    if (!rec) return
    const previous = rec
    // Optimistic: dismiss and mark-done apply immediately, roll back on failure.
    recommendation.setData({ ...rec, status })
    setSaving(true)
    setError(null)
    try {
      const updated = await api.recommendations.setStatus(id, status)
      recommendation.setData(updated)
      if (status !== 'open') navigate('recommendations')
    } catch (e) {
      recommendation.setData(previous)
      setError(e instanceof ApiFailure ? e : null)
    } finally {
      setSaving(false)
    }
  }

  if (screenState === 'loading') {
    return (
      <Screen id="recommendationDetail" state={screenState} back={{ screen: 'recommendations' }}>
        <LoadingRegion>
          <SkeletonCard />
          <SkeletonList rows={4} />
        </LoadingRegion>
      </Screen>
    )
  }

  if (!rec) {
    return (
      <Screen id="recommendationDetail" state="error" back={{ screen: 'recommendations' }}>
        <ErrorState
          titleKey="recommendationDetail.error.title"
          error={recommendation.state.status === 'error' ? recommendation.state.error : undefined}
          onRetry={recommendation.reload}
        />
      </Screen>
    )
  }

  const impacts: { key: string; label: string; value: string }[] = []
  if (rec.monthlyImpact && rec.monthlyImpact.amount !== 0) {
    impacts.push({
      key: 'monthly',
      label: t('recommendations.metric.potential'),
      value: t('recommendationDetail.impact.monthly', {
        amount: formatMoney(rec.monthlyImpact, { absolute: true }),
      }),
    })
  }
  if (rec.annualImpact && rec.annualImpact.amount !== 0) {
    impacts.push({
      key: 'annual',
      label: t('recurring.metric.annual'),
      value: t('recommendationDetail.impact.annual', {
        amount: formatMoney(rec.annualImpact, { absolute: true }),
      }),
    })
  }
  if (rec.oneTimeImpact && rec.oneTimeImpact.amount !== 0) {
    impacts.push({
      key: 'once',
      label: t('recommendations.metric.potential'),
      value: t('recommendationDetail.impact.once', {
        amount: formatMoney(rec.oneTimeImpact, { absolute: true }),
      }),
    })
  }
  if (rec.scoreGain) {
    impacts.push({
      key: 'score',
      label: t('dashboard.score.label'),
      value: t('recommendationDetail.impact.scoreGain', { points: rec.scoreGain }),
    })
  }

  const evidenceAccounts = (accounts.state.data ?? []).filter((account) =>
    rec.evidence?.accountIds?.includes(account.id),
  )
  const evidenceSeries = (series.state.data ?? []).filter((entry) =>
    rec.evidence?.recurringSeriesIds?.includes(entry.id),
  )

  return (
    <Screen
      id="recommendationDetail"
      state={screenState}
      title={recommendationTitle(rec)}
      back={{ screen: 'recommendations' }}
    >
      <div className="stack">
        {error ? (
          <ErrorState titleKey="recommendationDetail.error.title" error={error} onRetry={() => setError(null)} />
        ) : null}

        {impacts.length > 0 ? <ImpactCard items={impacts} /> : null}

        <section>
          <SectionHeader title={t('recommendationDetail.section.why')} />
          <GeneratedText
            text={rec.prose?.why}
            copyKey={`${rec.copyKey}.why` as CopyKey}
            params={displayParams(rec.params)}
            className="body-secondary"
          />
        </section>

        {/* recommendation.schema.json: prose.steps falls back to the
            deterministic `steps`, and if that is empty too the section is not
            rendered at all. */}
        {rec.prose?.steps || (rec.steps && rec.steps.length > 0) ? (
          <section>
            <SectionHeader title={t('recommendationDetail.section.steps')} />
            <GeneratedText
              text={rec.prose?.steps}
              copyKey="recommendationDetail.section.steps"
              lines={rec.steps ?? []}
              className="body-secondary"
            />
          </section>
        ) : null}

        {/* Everything analysisFailed adds here — quiet, inline, and only when
            another attempt could succeed. */}
        {proseState === 'analysisFailed' && prose.retryable ? (
          <div className="row">
            <Button
              variant="quiet"
              onClick={() => prose.retry({ target: 'recommendation', id: rec.id })}
            >
              {t('recommendationDetail.cta.retryExplain')}
            </Button>
          </div>
        ) : null}

        <section>
          <SectionHeader title={t('recommendationDetail.section.evidence')} />
          <List>
            {evidenceSeries.map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  className="list-row"
                  onClick={() => navigate('recurringDetail', { id: entry.id })}
                >
                  <span className="list-row-main">
                    <span className="list-row-title">{entry.name}</span>
                    <span className="list-row-sub">{t('recurring.title')}</span>
                  </span>
                </button>
              </li>
            ))}
            {evidenceAccounts.map((account) => (
              <li key={account.id}>
                <button
                  type="button"
                  className="list-row"
                  onClick={() => navigate('debtPlanner')}
                >
                  <span className="list-row-main">
                    <span className="list-row-title">{account.name}</span>
                    <span className="list-row-sub">{t('settings.row.accounts')}</span>
                  </span>
                </button>
              </li>
            ))}
            {(rec.evidence?.categories ?? []).map((category) => (
              <li key={category}>
                <button
                  type="button"
                  className="list-row"
                  onClick={() => navigate('categoryDetail', { id: category })}
                >
                  <span className="list-row-main">
                    <span className="list-row-title">{categoryName(category)}</span>
                    <span className="list-row-sub">{t('categories.title')}</span>
                  </span>
                </button>
              </li>
            ))}
            {(rec.evidence?.feeKinds ?? []).map((kind) => (
              <li key={kind ?? 'other'}>
                <button type="button" className="list-row" onClick={() => navigate('fees')}>
                  <span className="list-row-main">
                    <span className="list-row-title">{feeKindName(kind)}</span>
                    <span className="list-row-sub">{t('fees.title')}</span>
                  </span>
                </button>
              </li>
            ))}
            {evidenceTransactions.map((tx) => (
              <TransactionRow
                key={tx.id}
                transaction={tx}
                onSelect={() => navigate('transactionDetail', { id: tx.id })}
              />
            ))}
          </List>
        </section>

        {rec.status === 'done' && rec.statusChangedAt ? (
          <p className="caption positive">
            {t('recommendationDetail.done', { date: formatDate(rec.statusChangedAt.slice(0, 10)) })}
          </p>
        ) : null}

        <div className="row">
          <Button
            variant="primary"
            disabled={saving}
            onClick={() => void setStatus(rec.status === 'done' ? 'open' : 'done')}
          >
            {rec.status === 'done' ? t('recommendationDetail.cta.markNotDone') : t('recommendationDetail.cta.markDone')}
          </Button>
          <Button variant="quiet" disabled={saving} onClick={() => void setStatus('dismissed')}>
            {t('recommendationDetail.cta.dismiss')}
          </Button>
        </div>
      </div>
    </Screen>
  )
}
